/**
 * Single source of truth for FIVE LENS Enneagram product pricing and catalogue.
 * 
 * VAT compliance:
 * VAT_RATE is 0.15 (15%). Rand is shown in South Africa, Namibia, Botswana, Lesotho and Eswatini;
 * 15% VAT applies to South Africa only.
 * Services sold to non-SA buyers are treated differently; USD figures carry no VAT breakdown.
 * In accordance with South African VAT Act Section 65, all ZAR prices precompute and export
 * the excluding-VAT amount, the VAT amount, and the including-VAT amount.
 */

export const VAT_RATE = 0.15;

/**
 * Configuration constant: whether exported cross-border services sold to non-SA buyers
 * are zero-rated for VAT.
 * This is an assumption pending written confirmation from the company's accountant.
 * When true, USD prices carry no VAT breakdown.
 * When false, USD prices must render a VAT breakdown in the same dual-figure form the ZAR price uses.
 */
export const EXPORT_SERVICES_ZERO_RATED = true;

export interface ZarPricing {
  currency: 'ZAR';
  currencySymbol: 'R';
  amountExclVat: number;
  vatAmount: number;
  amountInclVat: number;
  formattedExclVat: string;
  formattedInclVat: string;
  formattedVat: string;
}

export interface UsdPricing {
  currency: 'USD';
  currencySymbol: '$';
  amount: number | null;
  formattedAmount: string | null;
  amountExclVat?: number;
  vatAmount?: number;
  amountInclVat?: number;
  formattedExclVat?: string;
  formattedInclVat?: string;
  formattedVat?: string;
}

export interface GbpPricing {
  currency: 'GBP';
  currencySymbol: '£';
  amount: number | null;
  formattedAmount: string | null;
}

export interface ProductSavings {
  amountExclVat: number;
  formattedExclVat: string;
  discountPercentage: number;
  usdAmount?: number;
  formattedUsd?: string;
}

export interface Product {
  id: string;
  name: string;
  zar: ZarPricing;
  usd: UsdPricing;
  gbp?: GbpPricing;
  savings?: ProductSavings;
  features?: string[];
  cta?: {
    label: string;
    href: string;
  };
  note?: string;
  featured?: boolean;
}

export type ProductCatalogue = Record<string, Product>;

export const ZAR_CURRENCY_REGIONS = ['za', 'na', 'bw', 'ls', 'sz'];
export const VAT_REGIONS = ['za'];

export function isZarRegion(regionCode?: string, locale?: string): boolean {
  if (regionCode) {
    const clean = regionCode.toLowerCase().trim();
    return ZAR_CURRENCY_REGIONS.includes(clean);
  }
  if (locale) {
    const clean = locale.toLowerCase().trim();
    return clean === 'en-za' || clean === 'en_za' || clean.endsWith('-za') || clean.endsWith('_za');
  }
  // Default for rest of the world is USD (false).
  return false;
}

export function isVatRegion(regionCode?: string, locale?: string): boolean {
  if (regionCode) {
    const clean = regionCode.toLowerCase().trim();
    return VAT_REGIONS.includes(clean);
  }
  if (locale) {
    const clean = locale.toLowerCase().trim();
    return clean === 'en-za' || clean === 'en_za' || clean.endsWith('-za') || clean.endsWith('_za');
  }
  // VAT only applies to South Africa.
  return false;
}

function formatRand(amount: number): string {
  return `R${amount.toLocaleString('en-ZA')}`;
}

function createZarPricing(amountExclVat: number): ZarPricing {
  const vatAmount = Math.round(amountExclVat * VAT_RATE);
  const amountInclVat = amountExclVat + vatAmount;
  return {
    currency: 'ZAR',
    currencySymbol: 'R',
    amountExclVat,
    vatAmount,
    amountInclVat,
    formattedExclVat: formatRand(amountExclVat),
    formattedInclVat: formatRand(amountInclVat),
    formattedVat: formatRand(vatAmount),
  };
}

function formatUsd(amount: number): string {
  return `$${amount.toLocaleString('en-US')}`;
}

function createUsdPricing(amount: number | null = null): UsdPricing {
  if (amount === null) {
    return {
      currency: 'USD',
      currencySymbol: '$',
      amount: null,
      formattedAmount: null,
    };
  }
  const vatAmount = Math.round(amount * VAT_RATE);
  const amountInclVat = amount + vatAmount;
  return {
    currency: 'USD',
    currencySymbol: '$',
    amount,
    formattedAmount: formatUsd(amount),
    amountExclVat: amount,
    vatAmount,
    amountInclVat,
    formattedExclVat: formatUsd(amount),
    formattedInclVat: formatUsd(amountInclVat),
    formattedVat: formatUsd(vatAmount),
  };
}

function formatGbp(amount: number): string {
  return `£${amount.toLocaleString('en-GB')}`;
}

function createGbpPricing(amount: number | null = null): GbpPricing {
  if (amount === null) {
    return {
      currency: 'GBP',
      currencySymbol: '£',
      amount: null,
      formattedAmount: null,
    };
  }
  return {
    currency: 'GBP',
    currencySymbol: '£',
    amount,
    formattedAmount: formatGbp(amount),
  };
}

/**
 * USD prices are set by a fixed internal peg of ZAR 10 = USD 1 applied to the
 * EXCLUDING-VAT rand amount. This is a deliberate international pricing decision
 * and NOT a market exchange rate, and must NOT be "corrected" to prevailing market rates.
 * VAT applies to South African sales only; USD prices carry no VAT breakdown.
 */
export const products: Record<string, Product> = {
  'practitioner-certification': {
    id: 'practitioner-certification',
    name: 'Practitioner Certification',
    zar: createZarPricing(12000),
    usd: createUsdPricing(1200),
    gbp: createGbpPricing(900),
    features: [
      '8-10 hours self-paced pre-course',
      'Six 3-hour live Zoom sessions',
      'Full diagnostic toolkit access',
    ],
    cta: {
      label: 'Register Now',
      href: '/register',
    },
  },
  'certification-bundle': {
    id: 'certification-bundle',
    name: 'Practitioner + Team Facilitator Bundle',
    zar: createZarPricing(24000),
    usd: createUsdPricing(2400),
    gbp: createGbpPricing(1800),
    savings: {
      amountExclVat: 6000,
      formattedExclVat: formatRand(6000),
      discountPercentage: 20,
      usdAmount: 600,
      formattedUsd: '$600',
    },
    features: [
      'Level 1 Practitioner Certification',
      'Level 2 Team Facilitator Certification',
      'Advanced team dynamics mapping',
    ],
    cta: {
      label: 'Secure Bundle',
      href: '/register',
    },
    featured: true,
  },
  'team-facilitator-certification': {
    id: 'team-facilitator-certification',
    name: 'Team Facilitator Certification',
    zar: createZarPricing(18000),
    usd: createUsdPricing(1800),
    gbp: createGbpPricing(1350),
    note: 'Requires FIVE LENS Practitioner Certification (Level 1)',
    features: [
      'Team effectiveness methodologies',
      'Resolving systemic conflict',
      'Enterprise deployment readiness',
    ],
    cta: {
      label: 'Register Now',
      href: '/register',
    },
  },
};

/**
 * Reports and team materials. Kept separate from `products` so certification
 * grids that loop over productList do not render them.
 */
export const reportsAndMaterials: Record<string, Product> = {
  'pfr-standard': {
    id: 'pfr-standard',
    name: 'Personal Feedback Report: Standard',
    zar: createZarPricing(490),
    usd: createUsdPricing(49),
    note: 'For one-on-one coaching and debriefs',
  },
  'pfr-premium': {
    id: 'pfr-premium',
    name: 'Personal Feedback Report: Premium',
    zar: createZarPricing(970),
    usd: createUsdPricing(97),
    note: 'For team sessions; one per participant',
  },
  'team-workbook': {
    id: 'team-workbook',
    name: 'Team Workbook',
    zar: createZarPricing(320),
    usd: createUsdPricing(32),
    note: 'One per workshop participant',
  },
  'facilitator-pack': {
    id: 'facilitator-pack',
    name: 'Facilitator Pack',
    zar: createZarPricing(2550),
    usd: createUsdPricing(255),
    note: 'One per TDD/TPP engagement; includes Team Skills content',
  },
};

/** Community coach rate ranges, excluding VAT. Coaches set their rate inside these. */
export const coachRateRanges = {
  coachingPerSession:   { zar: { min: 2000, max: 5000 },   usd: { min: 200, max: 500 } },
  facilitationPerDay:   { zar: { min: 12000, max: 30000 }, usd: { min: 1200, max: 3000 } },
  adminFeeRate: 0.2, // FIVE LENS keeps 20% of Platform Work, on the full fee
};

export const getReportOrMaterial = (key: string): Product | undefined =>
  reportsAndMaterials[key];

export const productList: Product[] = Object.values(products);

export const getProduct = (key: string): Product | undefined => products[key];

export const pricing = {
  vatRate: VAT_RATE,
  exportServicesZeroRated: EXPORT_SERVICES_ZERO_RATED,
  products,
  productList,
  getProduct,
  reportsAndMaterials,
  coachRateRanges,
  getReportOrMaterial,
  zarCurrencyRegions: ZAR_CURRENCY_REGIONS,
  vatRegions: VAT_REGIONS,
  isZarRegion,
  isVatRegion,
};

export default pricing;
